import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePresignUpload, s3ExtractError, type PresignUpload } from '@/api/s3';

/** How many files upload at once — S3 handles more, but a small pool keeps the
 *  browser's connection budget and the progress UI readable. */
const CONCURRENCY = 3;

export type UploadStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface UploadItem {
  id: string;
  name: string;
  /** Total bytes (from the File). */
  size: number;
  /** Bytes sent so far. */
  loaded: number;
  status: UploadStatus;
  error?: string;
}

/**
 * PUT one file straight to S3 with progress. Uses XMLHttpRequest (not fetch)
 * because only XHR exposes `upload.onprogress` — without it a large file gives
 * the user no signal that anything is happening.
 */
function xhrPut(
  target: PresignUpload,
  file: File,
  onProgress: (loaded: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(target.method, target.url, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    for (const [k, v] of Object.entries(target.headers ?? {})) xhr.setRequestHeader(k, v);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed (network error)')));
    xhr.addEventListener('timeout', () => reject(new Error('Upload failed (timed out)')));
    xhr.send(file);
  });
}

/**
 * Multi-file browser→S3 upload manager. Presigns then streams each file directly
 * to object storage (bytes never transit @mgmt), tracking per-file progress so
 * the UI can show "N of M" and a live bar. The objects listing is refreshed once
 * per batch (not per file) to avoid thrashing while an upload set is in flight.
 */
export function useObjectUpload(serviceId: string, bucket: string, prefix: string) {
  const presign = usePresignUpload(serviceId, bucket);
  const qc = useQueryClient();
  const [items, setItems] = useState<UploadItem[]>([]);
  const seq = useRef(0);

  const patch = (id: string, next: Partial<UploadItem>) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...next } : it)));

  async function uploadOne(file: File, id: string) {
    patch(id, { status: 'uploading' });
    try {
      const key = `${prefix}${file.name}`;
      const target = await presign.mutateAsync({
        key,
        content_type: file.type || 'application/octet-stream',
      });
      await xhrPut(target, file, (loaded) => patch(id, { loaded }));
      patch(id, { status: 'done', loaded: file.size });
    } catch (err) {
      patch(id, { status: 'error', error: s3ExtractError(err, 'Upload failed') });
    }
  }

  /** Queue a set of files and drain them through a small concurrency pool. */
  async function uploadFiles(files: File[]) {
    if (files.length === 0) return;
    const queued = files.map((file) => ({ file, id: String(++seq.current) }));
    setItems((prev) => [
      ...prev,
      ...queued.map(({ file, id }) => ({
        id,
        name: file.name,
        size: file.size,
        loaded: 0,
        status: 'queued' as const,
      })),
    ]);

    let next = 0;
    const worker = async () => {
      while (next < queued.length) {
        const cur = queued[next++];
        await uploadOne(cur.file, cur.id);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, queued.length) }, worker),
    );

    await qc.invalidateQueries({
      queryKey: ['s3', serviceId, 'bucket', bucket, 'objects'],
    });
  }

  /** Drop finished/failed rows from the panel (only when nothing is in flight). */
  const clearFinished = () =>
    setItems((prev) => prev.filter((it) => it.status === 'uploading' || it.status === 'queued'));

  const active = items.some((it) => it.status === 'uploading' || it.status === 'queued');

  return { items, uploadFiles, clearFinished, uploading: active };
}
